import { store } from "@redux/store";
import authActions from "@redux/auth/actions";
import sessionManager from "./sessionManager";
import { requestAuth } from "./authRequest";

let pending = null;
const sameSession = (left, right) => left.sessionEpoch === right.sessionEpoch && left.userId === right.userId;
const sessionChanged = () => Object.assign(new Error("The sign-in session changed. Please retry."), { name: "SessionChangedError" });

export function refreshSession({ signal } = {}) {
  const session = sessionManager.getSessionSnapshot();
  if (signal?.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"));
  if (!session.refresh || session.sessionResetting) return Promise.reject(sessionChanged());
  if (!pending || !sameSession(pending.session, session)) {
    const operation = { session };
    operation.promise = requestAuth("/auth/token/refresh", { refresh: session.refresh }).then(tokens => {
      const current = sessionManager.getSessionSnapshot();
      if (!sameSession(session, current) || current.sessionResetting || current.refresh !== session.refresh) throw sessionChanged();
      if (!tokens.access) throw new Error("The server did not return a refreshed session. Please try again.");
      store.dispatch(authActions.updateToken(tokens.access, tokens.refresh || session.refresh,
        tokens.access_token_expiration || tokens.accessTokenExpiration || session.accessTokenExpiration,
        tokens.refresh_token_expiration || tokens.refreshTokenExpiration || session.refreshTokenExpiration));
      return tokens;
    }).catch(async error => {
      if (error.status === 401 && sameSession(session, sessionManager.getSessionSnapshot())) await sessionManager.reset({ reason: "unauthorized" });
      throw error;
    }).finally(() => { if (pending === operation) pending = null; });
    pending = operation;
  }
  return pending.promise.then(tokens => {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return tokens;
  });
}

export async function retryUnauthorized(error, client) {
  const request = error.config;
  const current = sessionManager.getSessionSnapshot();
  if (request?.__sessionEpoch !== undefined && request.__sessionEpoch !== current.sessionEpoch) throw sessionChanged();
  if (!request || request._retry || !current.refresh || current.sessionResetting) {
    if (!current.sessionResetting) await sessionManager.reset({ reason: "unauthorized" });
    throw error;
  }
  request._retry = true;
  const previousAccess = request.__requestAccess;
  const tokens = previousAccess && current.access && previousAccess !== current.access
    ? { access: current.access } : await refreshSession({ signal: request.signal });
  request.headers = { ...request.headers, Authorization: `Bearer ${tokens.access}` };
  return client(request);
}
